import { Address } from "@origin-digital/address-lookup";
import { navTo } from "@origin-digital/event-dispatcher";
import { AccountDetails_viewer } from "../../kraken/graphql-types/AccountDetails";
import { useKrakenAccountDetails } from "../../kraken/queries";
import {
  FieldType,
  ProfileFieldMapResult,
  ProfileFieldMap,
  FIELD_UNSUPPORTED,
} from "../../profileFieldMap";
import {
  useUpdateContactPreference,
  useUpdateDob,
  useUpdateEmail,
  useUpdateLandline,
  useUpdateMobile,
  useUpdateUserRichAddress,
} from "../../kraken/mutations";
import { ContactPreferenceProp } from "../../../screens/UpdateContactPrefsScreen/ContactOption";
import { CommsDeliveryPreference } from "../../kraken/graphql-types/globalTypes";
import { convertQasAddressToRichAddressInput } from "../../../screens/UpdateAddress/UpdateAddressScreen/helpers";
import { formStack } from "../../../consts";
import { isDobSet } from "../../../utils/helpers";
import {
  mapToAddressProps,
  mapToBusinessDetailsProp,
  mapToContactPreferenceProps,
} from "./krakenDataConverter";

export const mapFieldsWithKraken = (
  viewer: AccountDetails_viewer | null | undefined
): ProfileFieldMap | undefined => {
  if (!viewer) {
    return undefined; // is loading or in error
  }

  return {
    [FieldType.NAME]: {
      data: viewer.fullName,
      useUpdate: () => [
        () => navTo.feedback(formStack.updateName),
        { success: true, loading: false, error: undefined },
      ],
      editable: true,
    },
    [FieldType.DOB]: {
      data: viewer.dateOfBirth,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateDob();
        return [
          (value: string) =>
            update({
              variables: {
                dateOfBirth: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateUser?.viewer?.dateOfBirth,
            error,
            loading,
          },
        ];
      },
      // DOB is only editable when not yet set. Once entered, it becomes
      // immutable for data integrity (GROOT-267).
      editable: !isDobSet(viewer.dateOfBirth),
    },
    [FieldType.EMAIL]: {
      data: viewer.email,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateEmail();
        return [
          (value: string) =>
            update({
              variables: {
                email: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateUser?.viewer?.email,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.MOBILE]: {
      data: viewer.mobile,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateMobile();
        return [
          (value: string) =>
            update({
              variables: {
                phone: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateUser?.viewer?.mobile,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.LANDLINE]: {
      data: viewer.landline,
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateLandline();
        return [
          (value: string) =>
            update({
              variables: {
                phone: value,
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateUser?.viewer?.landline ||
                  data?.updateUser?.viewer?.landline === "",
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.HOME_PHONE]: FIELD_UNSUPPORTED,
    [FieldType.BILLING_ADDRESS]: {
      data: mapToAddressProps(viewer.accounts),
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateUserRichAddress();
        const accountsResponse = data?.updateUserRichAddress?.viewer?.accounts;
        return [
          (value: Address) =>
            update({
              variables: {
                address: convertQasAddressToRichAddressInput(value),
              },
            }),
          {
            success:
              data === undefined
                ? undefined
                : Array.isArray(accountsResponse) &&
                  accountsResponse.length > 0,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.CONTACT_PREFERENCE]: {
      data: mapToContactPreferenceProps(viewer),
      useUpdate: () => {
        const [update, { data, loading, error }] = useUpdateContactPreference();
        return [
          (value: ContactPreferenceProp) => {
            if (!value.preference) {
              return;
            }
            return update({
              variables: {
                accountNumber: value.id!,
                commsDeliveryPreference:
                  CommsDeliveryPreference[value.preference],
              },
            });
          },
          {
            success:
              data === undefined
                ? undefined
                : !!data?.updateCommsDeliveryPreference?.account
                    ?.commsDeliveryPreference,
            error,
            loading,
          },
        ];
      },
      editable: true,
    },
    [FieldType.BUSINESS_ACCOUNT]: {
      data: mapToBusinessDetailsProp(viewer.accounts),
    },
  };
};

export const useKrakenProfileFieldMap: () => ProfileFieldMapResult = () => {
  const { data, refetch, error, loading } = useKrakenAccountDetails();
  return {
    fieldMap: mapFieldsWithKraken(data?.viewer),
    refetch,
    error,
    loading,
  };
};
